import restClient from "api/restClient";

/**
 * Keys that belong in the GraphQL request *body*. Everything else in a params
 * object (allow_normal, allow_deleted, commit, migrate, …) is a transport
 * control flag and stays in the query string, where the backend reads it.
 */
const BODY_KEYS = ["query", "subscription", "variables", "operationName"] as const;

type GraphqlParams = Record<string, any>;

/**
 * Split a legacy `restClient.get('/graphql', { params })` params object into the
 * GraphQL body (query/subscription/variables/operationName) and the remaining
 * control query params (allow_normal/allow_deleted/commit/migrate/…).
 *
 * `variables` is normalised to an object: several call sites pre-`JSON.stringify`
 * it (that was needed for the old GET query-string encoding), but the POST body
 * schema requires an object.
 */
const splitParams = (params: GraphqlParams = {}) => {
    const body: GraphqlParams = {};
    const controlParams: GraphqlParams = {};

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined) {
            return;
        }
        if ((BODY_KEYS as readonly string[]).includes(key)) {
            if (key === "variables" && typeof value === "string") {
                body[key] = JSON.parse(value);
            } else {
                body[key] = value;
            }
        } else {
            controlParams[key] = value;
        }
    });

    return { body, controlParams };
};

/**
 * Send a GraphQL query or mutation over POST.
 *
 * GraphQL must never travel in a URL query string: it leaks the operation (and
 * any credentials/role changes) into browser history, proxy logs and server
 * access logs, and — since the session cookie is SameSite=Lax — a mutation
 * reachable over GET is a CSRF privilege-escalation vector. The backend rejects
 * queries/mutations on GET; use this helper instead of `restClient.get('/graphql')`.
 *
 * Accepts the same params object the old GET call sites built, so migration is a
 * drop-in: `restClient.get('/graphql', { params })` → `graphqlPost(params)`.
 *
 * @param params GraphQL params (query/variables/… plus optional control flags).
 * @param config Extra axios request config (e.g. `signal` for cancellation).
 */
export const graphqlPost = (params?: GraphqlParams, config: Record<string, any> = {}) => {
    const { body, controlParams } = splitParams(params);
    return restClient.post("/graphql", body, { ...config, params: controlParams });
};

export default graphqlPost;
